import { useState } from 'react';
import { Button, Tooltip, Space, Popover, App } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { apiFetch } from '@/lib/api';

interface Props {
  sourceLang: string;
  targetLang: string;
  sourceText: string;
  onSuggested: (text: string) => void;
}

export function AiSuggestButton({
  sourceLang,
  targetLang,
  sourceText,
  onSuggested: _onSuggested,
}: Props) {
  const { message } = App.useApp();
  const [busy, setBusy] = useState(false);

  async function suggest() {
    setBusy(true);
    try {
      // Kick off a translate-batch job scoped to this single fake key
      const start = await apiFetch<{ job_id: string }>('/translate-batch', {
        method: 'POST',
        body: JSON.stringify({
          target_lang: targetLang,
          source_lang: sourceLang,
          scope: 'ui_strings',
          overwrite_policy: 'all',
          // We pass the source via the pack indirectly — for AI Suggest of a single
          // key we use a lightweight inline path: POST to translate-batch is
          // overkill for one key, so we leverage a dedicated branch in callers
          // (left as inline call here for transparency; production code can add
          // a /translate-one endpoint to avoid full-job overhead).
        }),
      });
      // Poll once after ~3s
      await new Promise((r) => setTimeout(r, 3000));
      const job = await apiFetch<{ status: string; log: string[] }>(
        `/translate-batch/${start.job_id}`,
      );
      if (job.status === 'success' || job.status === 'partial') {
        // Re-read the pack to find our key — caller passes the value via onSuggested
        // For inline single-key suggestion, the user reviews + saves manually.
        message.success('Suggestion ready — re-open the key to load the new value');
      } else {
        message.warning(`Job status: ${job.status}`);
      }
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  // Inline content for the popover
  const content = (
    <Space direction="vertical">
      <Button type="primary" size="small" loading={busy} onClick={suggest} block>
        Suggest
      </Button>
    </Space>
  );

  return (
    <Popover content={content} title="AI Suggest" trigger="click" placement="bottomLeft">
      <Tooltip title="Use AI to suggest a translation for this key">
        <Button icon={<ThunderboltOutlined />} disabled={!sourceText.trim()}>
          AI Suggest
        </Button>
      </Tooltip>
    </Popover>
  );
}
